import { Link } from '@material-ui/core';
import { PadCollapse } from 'app/shared/components/misc';
import styled from 'styled-components/macro';
import Rating from '@material-ui/lab/Rating';
import styledFuncs from 'app/styles/styledFuncs';
import Body from 'app/shared/components/typography/body/Body';
import ClassName from 'app/styles/ClassName';
import clsx from 'clsx';
import AppImage from 'app/shared/components/app-image/AppImage';
import { PropsOf } from 'app/tools/general.types';
import Button from 'app/shared/components/button/Button';
import styledMixins from 'app/styles/styledMixins';
import { Merge } from 'type-fest';
import { getErrorMsg } from 'app/core/errors/error-utils';
import Icon from 'app/shared/components/icon/Icon';
import IconLib, {
  IWRepairerSvg,
  LocationSvg,
} from 'app/shared/components/icon/icon-lib';
// import { RepairCase } from 'app/core/api/repairer/api-repairer.types';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
// import ProductPictureOrDefault from 'app/shared/components/app-image/ProductPictureOrDefault';
// import { productImg } from 'app/core/api/product-assignment/api-product-assignment.types';
// import { Divider } from '@material-ui/core';
import { FrameContentsInner } from 'app/shared/frames/ViewFrame';
// import { RepairerSearchItem } from 'app/core/api/repairer/api-repairer.types';
// import { RepairerInfo } from 'app/core/api/user/api-user.types';
import {
  useDeleteChat,
  useGetRepairerInfo,
} from 'app/core/api/user/api-user';
import SharedRoutePath from 'app/shared/SharedRoutePath';
import { useHistory } from 'react-router-dom';
import { useUrlParams } from 'app/tools/hooks/misc-hooks';
import { useEffect } from 'react';
import { useRepairerCase } from 'app/core/api/repairer/api-repairer';
import { CaseStatus } from 'app/core/api/repairer/api-repairer.types';

const SectionBox: React.FC<{ title: string }> = ({
  title,
  children,
}) => (
  <>
    <h3
      className={clsx(
        'mt-1 mb-0 font-normal text-primary',
        ClassName.NO_HEIGHT_MARGIN
      )}
    >
      {title}
    </h3>
    <div className="flex flex-col rounded border border-solid border-neutral-200">
      {children}
    </div>
  </>
);

const SectionButton: React.VFC<
  Merge<
    PropsOf<typeof Button>,
    { title: string; subtitle: string; icon: IconLib }
  >
> = ({ title, subtitle, icon, ...buttonProps }) => (
  <Button variant="text" className="flex-grow h-btn" {...buttonProps}>
    <Icon {...{ icon }} />
    <div className="flex-grow ml-1">
      <h4 className="m-0 mb-0.2 text-sm font-semibold text-left line-clamp-1">
        {title}
      </h4>
      <div className="text-xs font-light text-left line-clamp-2">
        {subtitle}
      </div>
    </div>
    <Icon icon={IconLib.CHEVRON_RIGHT} />
  </Button>
);

const RepairerProfileImg = styled(AppImage)`
  height: 115px;
  width: 115px;
  margin: 0 auto;
  overflow: hidden;
  > * {
    ${styledMixins.roundCorners};
  }
`;

const DetailsTitle = styled(Body)`
  font-weight: 400;
  font-size: 16px;
  color: #31ad31;
  + * {
    margin: ${styledFuncs.pad(0.5)} ${styledFuncs.pad(1.5)} 0;
  }
`;
// const LocationContainer = styled.div`
//   padding: ${styledFuncs.pad()};
// `;
const LocationBoundingBox = styled.div`
  ${styledMixins.roundCorners}
  background: white;
  box-shadow: 0 0 3px #ddd;
  overflow: hidden;
`;
const LocationCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;
const Location = styled.div`
  display: flex;
  flex-flow: row;
  position: relative;
  font-size: 1rem;
  line-height: 1.5em;
  height: calc(4.5em + ${styledFuncs.pad()});
  align-items: center;
`;
const LocationDetails = styled.div`
  margin: ${styledFuncs.pad()};
  margin-bottom: ${styledFuncs.pad(1.5)};
  margin-top: ${styledFuncs.pad(2)};
  flex: 1 1 auto;
  ${styledMixins.lineClamp(3)}
  > div {
    text-align: left;
    font-weight: 400;
    font-size: 13px;
    line-height: 1.4em;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;
const StyledIcon = styled(Icon)`
  flex: 0 0 auto;
  margin-left: ${styledFuncs.pad(1)};
  padding: ${styledFuncs.pad(0.5)};
`;
const LocationButton = styled(Button)`
  margin: 0;
  height: calc(4.5em + ${styledFuncs.pad()});
  width: 100px;
  border-radius: 0 10px 10px 0;
  padding: 0;
`;
const RepairerRating = styled.div`
  display: flex;
  align-items: center;
  padding-left: ${styledFuncs.pad(0.0)};
  margin-left: ${styledFuncs.pad(6)};
  margin-bottom: ${styledFuncs.pad(0.5)};
  > span {
    margin: 0 ${styledFuncs.pad(0.5)} 0 0;
    font-weight: 500;
    font-size: 14px;
    color: black;
  }
`;
const NewRepairer = styled.div`
  div {
    font-weight: 500;
    font-size: 10px;
    color: #6abc35;
  }
`;

export default function ProfileDrawer(): JSX.Element {
  const { showDialog, showSnack } = useShowInfo();
  const { id } = useUrlParams(['id']);
  const { request$: deleteAssignedChat$ } = useDeleteChat();
  const { resource: repairerInfo, request: loadRepairInfo$ } =
    useGetRepairerInfo();
  const { resource: repairCase } = useRepairerCase(id);

  useEffect(() => {
    loadRepairInfo$(Number(id));
    deleteAssignedChat$(Number(id));
  }, [deleteAssignedChat$, id, loadRepairInfo$]);

  const history = useHistory();

  const handleDeleteError = (e: unknown) => {
    showSnack({ message: getErrorMsg(e), alertType: 'error' });
  };

  const handleDeleteSuccess = () => {
    showSnack({
      message: 'Chat deleted',
      alertType: 'error',
    });
    history.replace(SharedRoutePath.HOME);
    // should redirect to product details page
    // refresh();
  };

  const handleConfirmDelete = () =>
    deleteAssignedChat$(Number(id)).subscribe({
      next: handleDeleteSuccess,
      error: handleDeleteError,
    });

  const deleteChat = () => {
    showDialog({
      title: 'Delete chat',
      message: `Are you sure you want to delete this? Pressing delete will terminate this chat and allow you to log an issue and search for another repairer.`,
      actions: [
        { text: 'Cancel' },
        {
          text: 'Delete',
          callback: () => {
            handleConfirmDelete();
          },
        },
      ],
    });
  };

  return (
    <FrameContentsInner>
      {/* <Drawer
        anchor="bottom"
        open={isRepairerDrawerOpen}
        onClose={() => setIsRepairerDrawerOpen(false)}
      > */}
      <PadCollapse pad={0} />
      <div
        style={{
          display: 'block',
          margin: '0 40px 50px',
        }}
      >
        <div style={{ display: 'block', margin: '20px' }}>
          <RepairerProfileImg
            src={repairerInfo?.imageUrl}
            alt="repairer photo"
            fallbackImgNode={<IWRepairerSvg width="55" height="55" />}
          />
        </div>

        {/* <div
          style={{
            display: 'flex',
            alignItems: 'stretch',
          }}
        > */}
        {/* <div style={{ width: '48.5%', textAlign: 'center' }}>
            <DetailsTitle>Distance</DetailsTitle>
            <Body small>
              {/* {repairer.distance.toFixed(1)}  X km away
            </Body>
          </div>
          <div style={{ width: '1%' }}>
            <Divider
              orientation="vertical"
              flexItem
              style={{
                height: '100%',
                width: '1px',
                backgroundColor: '#000000',
              }}
            />
          </div>
          <div style={{ width: '48.5%', textAlign: 'center' }}> */}
        <div style={{ textAlign: 'center' }}>
          <DetailsTitle>Rating</DetailsTitle>
          {repairerInfo?.rating ? (
            <RepairerRating>
              <span>{repairerInfo?.rating}</span>
              <div style={{ textAlign: 'center' }}>
                <Rating value={repairerInfo?.rating} readOnly />
              </div>
            </RepairerRating>
          ) : (
            <NewRepairer>
              <div
                className="repairer"
                style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  margin: '5px 0',
                }}
              >
                New Repairer
              </div>
            </NewRepairer>
          )}
        </div>
        {/* </div> */}
      </div>
      {/* </div> */}
      <SectionBox title="Shop Location">
        <LocationBoundingBox>
          <LocationCard>
            <Location>
              <StyledIcon icon={IconLib.HOME} />
              <LocationDetails>
                {repairerInfo?.address}
              </LocationDetails>
              <div className="top-0 right-0 leading-none text-primary text-[0.7rem]">
                <Link
                  target="_blank"
                  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                  href={`https://maps.google.com/?q=${repairerInfo?.address}`}
                >
                  <LocationButton slim>
                    <LocationSvg />
                  </LocationButton>
                </Link>
              </div>
            </Location>
          </LocationCard>
        </LocationBoundingBox>
      </SectionBox>
      <SectionBox title="Chat Settings">
        <SectionButton
          title="Delete Chat"
          subtitle="Permanently deletes this chat"
          onClick={deleteChat}
          disabled={repairCase?.caseStatus === CaseStatus.IN_PROGRESS}
          icon={IconLib.TRASH}
        />
      </SectionBox>

      {/* </Drawer> */}
    </FrameContentsInner>
  );
}
